import type { AssetClassConstants } from '@/constants/AssetClassConstants';
import type { Currency } from '@/constants/Currency';
import type { FactorConstants } from '@/constants/FactorConstants';
import type { Region } from '@/constants/Region';
import type { StyleConstants } from '@/constants/StyleConstants';
import type { PortfolioTreeParametersDTO } from '@/types/IPortfolioTree';
import type { PossiblyLiveObject } from '@/types/LiveObjectAccessRightDTO';
import type { PortfolioType } from '@/types/portfolio';
import type { FxConversion } from '@/constants/FxConversion';

export type DSBPortfolioItemResponseDTOBase = PortfolioItemResponseDTO & {
  virtualKey: string;
  displayName: string;
  favoriteId?: string;
};

export type DSBPortfolioItemResponseDTO =
  | DSBPortfolioItemResponseDTOBase
  | (DSBPortfolioItemResponseDTOBase & PossiblyLiveObject);

export interface PortfolioItemResponseDTO {
  portfolioId: string;
  oldPortfolioId: number;
  slug: string;
  name: string;
  type: PortfolioType;
  isBenchmark: boolean;
  assetClass: AssetClassConstants | null;
  factor: FactorConstants | null;
  region: Region | null;
  currency: Currency | null;
  fxType: FxConversion | null;
  style: StyleConstants | null;
  reference: string | null;
  userReference: string | null;
  historyStartDate: string | null;
  inceptionDate: string | null;
  numStrategies: number;
  teamId: number;
  description: string | null;

  /**
   * shouldExposePositions is a flag that we have on the portfolio_list table in the database
   * true: this live portfolio/strategy can be viewed in the analytics page as a portfolio
   * false: this strategy can only be viewed as a strategy, not as a portfolio and has access to only the strategy analytics pages
   */
  shouldExposePositions: boolean | null;

  /**
   * It's the portfolioId we manually set in the useDiscoverStore.ts and useDataDiscovery.ts
   */
  virtualKey?: string;

  /**
   * Parameters stored in the portfolios table
   * Determines certain behaviors of the portfolio tree
   * Added to know whether a portfolio is live or not in useDataDiscovery.ts
   */
  parameters: PortfolioTreeParametersDTO | null;
}

export const isPortfolioItemResponseDTO = (data: unknown): data is PortfolioItemResponseDTO => {
  return typeof data === 'object' && data !== null && 'portfolioId' in data && 'slug' in data;
};
